import { useEffect, useRef, useState } from 'react'
import type { DefaultProps } from 'typings'
import { useAnalytics } from 'use-analytics'
import { sumByKey } from '../utils/sumByKey'
import { usePromotionsListState } from './usePromotionsListState'

export function AnalyticsWrapper(props: DefaultProps) {
  const { track } = useAnalytics()
  const pageRef = useRef(false)
  const ready = pageRef.current

  const trackIfReady: typeof track = (...params) => {
    return new Promise(() => ready && track(...params))
  }

  const {
    viewState: { status },
    filtersState,
    tableState: { data: items, sortState },
    originalItems,
    paginationState: { currentPage: page },
    searchState: { debouncedValue: searchTerm },
  } = usePromotionsListState()

  const [prevFiltersState, setPrevFiltersState] = useState(filtersState)

  const filterRatio = items.length / originalItems.length

  useEffect(() => {
    pageRef.current =
      !pageRef.current && ['empty', 'ready', 'not-found'].includes(status)
  }, [status])

  useEffect(() => {
    ready &&
      trackIfReady('Promotions Listed', {
        items: items.length,
        itemsByType: sumByKey(items, 'type'),
        itemsByStatus: sumByKey(items, 'status'),
        page,
        appliedFilters: [
          ...(searchTerm ? ['textSearch'] : []),
          ...filtersState.filter((f) => f.values.length).map((f) => f.id),
        ],
      })
  }, [ready])

  useEffect(() => {
    trackIfReady('Search Performed', {
      length,
      filterRatio,
    })
  }, [searchTerm])

  useEffect(() => {
    trackIfReady('Page Changed', { page })
  }, [page])

  useEffect(() => {
    filtersState.forEach((filter) => {
      const prevFilter = prevFiltersState.find((f) => f.id === filter.id)

      const hasChanged =
        filter.values.length !== prevFilter?.values.length ||
        filter.values.some((v) => !prevFilter.values.includes(v))

      if (hasChanged) {
        trackIfReady('Filter Applied', {
          type: filter.id,
          values: filter.values,
          filterRatio,
        })
      }
    })

    setPrevFiltersState(filtersState)
  }, [items])

  const { order, by } = sortState

  useEffect(() => {
    trackIfReady('Sorted Promotions', {
      direction: order,
      column: by,
    })
  }, [order, by])

  return <>{props.children}</>
}
